import * as React from "react";
import styled, { css, withTheme } from "styled-components";
import color from "color";

import { Caret } from "@ui/Assets/Symbolicons/Caret";
import { ITheme } from "@ui/themes/ThemeInterface";
import { rem, transition } from "@ui/helpers";
import { TestWrapper } from "@ui/Atoms";

interface DropdownTriggerProps {
  active?: boolean;
  className?: string;
  children?: React.ReactNode;
  isDark?: boolean;
  isDarkOutline?: boolean;
  theme: ITheme;
  onClick: (event: React.SyntheticEvent) => void;
  toLeft?: boolean;
  testId?: string;
  withWideTrigger?: boolean;
  smallPaddings?: boolean;
  variant?: "borderless" | "default";
}

const Trigger = withTheme((props: DropdownTriggerProps) => {
  const caretColor = React.useMemo(() => {
    if (props.variant === "borderless") {
      return props.theme.palette.blue500;
    }

    if (props.isDark) {
      return props.theme.palette.grey50;
    }

    return props.theme.palette.grey600;
  }, [props.variant, props.isDark]);

  return (
    <TestWrapper testId={props.testId}>
      <button className={props.className} onClick={props.onClick}>
        {props.children}{" "}
        <StyledCaret
          size={props.theme.icons.sizes.base_x3}
          variant="line"
          active={props.active}
          noMargin={props.smallPaddings}
          color={caretColor}
        />
      </button>
    </TestWrapper>
  );
});

const DropdownTrigger = styled(Trigger)<DropdownTriggerProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: ${transition(["background, border, color"])};
  border-radius: ${props => props.theme.buttons.borderRadius};
  position: relative;
  z-index: 1;
  height: ${rem(40)};
  white-space: nowrap;

  padding-left: ${props =>
    props.smallPaddings
      ? props.theme.margins.base
      : props.theme.margins.base_x2};
  padding-right: ${props =>
    props.smallPaddings
      ? props.theme.margins.base
      : props.theme.margins.base_x2};

  min-width: ${props => (props.withWideTrigger ? rem(104) : "inherit")};

  margin-left: ${({ toLeft = false }) => (toLeft ? 0 : "auto")};

  color: ${props =>
    props.isDark ? props.theme.palette.grey50 : props.theme.palette.grey600};
  background: ${props => props.active && props.theme.palette.grey50};

  ${props =>
    ((props.active && props.isDark) || (props.active && props.isDarkOutline)) &&
    `
    background: transparent;
  `}

  border: ${rem(1)} solid
    ${props =>
    props.isDark
      ? color(props.theme.palette.white).alpha(0.2).toString()
      : color(props.theme.palette.grey700).alpha(0.2).toString()};

  ${props =>
    props.isDarkOutline &&
    `
      border: ${rem(1)} solid ${props.theme.palette.grey800};
      color: ${props.theme.palette.grey800};
  `}

  cursor: pointer;

  transition: ${transition(["color", "border", "background"])};

  &:active,
  &:focus {
    outline: 0;
    border: ${rem(1)} solid ${props => props.theme.palette.blue500};

    ${props =>
      !props.isDark &&
      `
      color: ${props.theme.palette.grey800};
      svg {
        fill: ${props.theme.palette.grey800};
      }
    `}
  }

  & svg {
    fill: ${props =>
      props.isDark ? props.theme.palette.grey50 : props.theme.palette.grey600};
  }

  &:hover {
    ${props =>
      props.isDark
        ? `
      border: ${rem(1)} solid ${color(props.theme.palette.white)
            .alpha(0.3)
            .toString()};
    `
        : `
    background: ${
      props.isDarkOutline ? "transparent" : props.theme.palette.grey50
    };
    border: ${rem(1)} solid ${props.theme.palette.grey600};
    color: ${props.theme.palette.grey800};

    svg {
      fill: ${props.theme.palette.grey800};
    }
  `}
  }

  ${props =>
    props.variant === "borderless" &&
    css`
      &,
      &:active,
      &:focus,
      &:hover {
        border: none;
        background: transparent;
        padding: 0;
        height: auto;
      }
    `}
`;

const StyledCaret = styled(Caret)<{ active?: boolean; noMargin?: boolean }>`
  margin-left: ${props => (props.noMargin ? 0 : props.theme.margins.base)};
  margin-right: -${props => props.theme.margins.base};
  transform: ${props => (props.active ? "rotate(180deg)" : "none")};
  transition: ${transition(["transform"])};
`;

export default DropdownTrigger;
